import type { ESignature, PendingCharts } from "@vaulted/shared";
import type { QueryClient } from "@tanstack/react-query";
import type { NavigateFunction } from "react-router";
import { get } from "../api";
import { Checkbox, formatDateTime24 } from "./ui";

/** A signature as kept on a chart or review: their signature (or name), credentials, when, and where from. */
export function SignatureBlock({ title, sig }: { title: string; sig: ESignature }) {
  return (
    <div className="flex flex-col gap-1.5 text-sm">
      <p className="font-semibold">{title}</p>
      {sig.image ? (
        <img src={sig.image} alt={`Signature of ${sig.name}`} className="max-h-16 w-auto max-w-[240px] self-start rounded border border-mist bg-white object-contain p-1" />
      ) : (
        <p className="font-display text-xl italic">{sig.name}</p>
      )}
      <p>
        <span className="font-semibold">{sig.name}</span>
        {sig.credentials && `, ${sig.credentials}`} · {formatDateTime24(sig.at)}
      </p>
      <p className="text-xs text-muted">“{sig.statement}”</p>
      <p className="text-xs text-muted">
        Signed electronically{sig.ip ? ` from ${sig.ip}` : ""} · record {sig.hash.slice(0, 12)}
      </p>
    </div>
  );
}

/** The statement they tick before signing (the server refuses without it). */
export function AgreeBox({ statement, checked, onChange }: { statement: string; checked: boolean; onChange: (v: boolean) => void }) {
  return <Checkbox label={statement} description="Your name, credentials, and saved signature (Account) are added with the date and time." checked={checked} onChange={onChange} />;
}

/** After a review: on to the next chart waiting for this reviewer, or back to Pending Charts when there are none. */
export async function goToNextReview(qc: QueryClient, nav: NavigateFunction, doneId: string) {
  const pending = await qc.fetchQuery({ queryKey: ["charts-pending"], queryFn: () => get<PendingCharts>("/api/charts/pending"), staleTime: 0 });
  const next = pending.toReview.find((x) => x.id !== doneId);
  nav(next ? (next.source ? `/records/${next.id}` : `/charts/${next.id}`) : "/pending-charts", { replace: true });
}
